import 'mapbox-gl/dist/mapbox-gl.css';
import { useRef, useEffect, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import type { RestaurantWithEventSpaces } from '@/types/restaurant';
import { useMapInitialization } from './map/useMapInitialization';
import { useMapMarkers } from './map/useMapMarkers';
import { useToast } from '@/hooks/use-toast';
import { Locate } from 'lucide-react';
import { Button } from './ui/button';

interface RestaurantsMapboxProps {
  restaurants: RestaurantWithEventSpaces[];
  isVisible: boolean;
}

export const RestaurantsMapbox = ({ restaurants, isVisible }: RestaurantsMapboxProps) => {
  const { toast } = useToast();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);

  // Default to NYC coordinates
  const defaultCenter: [number, number] = [-73.935242, 40.730610];

  // Memoize valid restaurants to prevent unnecessary marker updates
  const validRestaurants = useMemo(() => {
    return restaurants.filter(restaurant => {
      const hasValidCoordinates = restaurant.latitude && 
        restaurant.longitude && 
        !isNaN(Number(restaurant.latitude)) && 
        !isNaN(Number(restaurant.longitude));
      
      if (!hasValidCoordinates) {
        console.log('Restaurant missing valid coordinates:', restaurant.name);
      }
      return hasValidCoordinates;
    });
  }, [restaurants]);

  // Clear markers utility function
  const clearMarkers = () => {
    markers.current.forEach(marker => marker.remove());
    markers.current = [];
  };

  const { isLoading, error } = useMapInitialization(mapContainer, map, defaultCenter, isVisible);
  
  useMapMarkers(map, validRestaurants, markers, isLoading, clearMarkers);

  // Handle errors with toast
  useEffect(() => {
    if (error) {
      console.error('Map error:', error);
      toast({
        title: "Error loading map",
        description: error.message,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Recenter map handler
  const handleRecenter = () => {
    if (!map.current) return;
    
    map.current.easeTo({
      center: defaultCenter,
      zoom: 12,
      duration: 1000
    });
  };

  return (
    <div 
      className={`
        relative w-full rounded-lg overflow-hidden shadow-md transition-opacity duration-300 ease-in-out
        ${isVisible ? 'opacity-100 md:h-[calc(100vh-18rem)] h-[65vh]' : 'opacity-0'}
      `}
      style={{ 
        height: isVisible ? undefined : '0px',
        visibility: isVisible ? 'visible' : 'hidden'
      }}
    >
      <div ref={mapContainer} className="w-full h-full" />
      {isLoading && (
        <div className="absolute inset-0 bg-accent/50 flex items-center justify-center">
          <p className="text-muted-foreground">Loading map...</p>
        </div>
      )}
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-4 left-4 z-10 bg-white hover:bg-gray-50 text-gray-700 shadow-md hover:shadow-lg transition-all duration-200"
        onClick={handleRecenter}
        title="Recenter map"
      >
        <Locate className="h-4 w-4" />
      </Button>
    </div>
  );
};